import Modal from '@components/Modal';
import { useSectionMutations } from '@ContractBuilder/hooks/useSectionMutations';
import { useSectionModal } from '@ContractBuilder/modules/navigation';
import { getSectionLabel } from '@ContractBuilder/modules/navigation/modules/section-modal/utils/get-section-label';
import { SectionForm } from '@ContractBuilder/modules/navigation/modules/section-modal/views/SectionForm';
import { useEntityStore } from '@ContractBuilder/store';
import { useForm } from '@hooks/form';
import { useDeepCompareMemo } from '@hooks/use-deep-compare-memo';
import { useLanguage } from '@root/src/language';

import { sectionSchema } from '../schema';
import type { SectionModalFormState } from '../types';
import { getInitialValues } from '../utils/get-initial-state';

export const SectionModalController = () => {
  'use memo';

  const { submission } = useEntityStore(({ submission }) => ({ submission }));
  const { updateSection, createSection } = useSectionMutations();

  const { getContent } = useLanguage({ prefix: 'naming.mrc.modals.editSection' });

  const { activeSectionId, isSectionModalOpen, closeSectionModal } = useSectionModal();
  const isEditing = activeSectionId !== undefined;
  const modalMode = isEditing ? 'edit' : 'create';

  const handleFormSubmit = async (formValues: SectionModalFormState) => {
    if (!submission) {
      return;
    }

    const section = submission.sections.find((item) => item.id === activeSectionId);
    const { name, standardisedSectionName, shouldHideTitleInPdf, shouldResetPageCounter } = formValues;

    const payload = {
      label: getSectionLabel(name, standardisedSectionName) as string,
      standardised_name: standardisedSectionName,
      should_hide_title_in_pdf: shouldHideTitleInPdf,
      should_reset_page_counter: shouldResetPageCounter,
    };

    if (section && isEditing) {
      await updateSection.mutateAsync({ sectionId: activeSectionId, payload });
    } else {
      await createSection.mutateAsync(payload);
    }

    return closeSectionModal();
  };

  const initialState = useDeepCompareMemo(
    () => getInitialValues(submission, activeSectionId),
    [submission, activeSectionId],
  );

  const { getFieldProps, onSubmit, isLoading } = useForm<SectionModalFormState>({
    initialState,
    onSubmit: handleFormSubmit,
    validationSchema: sectionSchema,
    shouldEnableReinitialization: true,
  });

  const handleSubmit = async () => {
    await onSubmit();
  };

  return (
    <Modal
      className="w-[572px] gap-0 sm:max-w-[572px]"
      data-testid="section-modal"
      onClose={closeSectionModal}
      open={isSectionModalOpen}
      shouldShowCloseIcon={false}
      title={getContent(`title.${modalMode}`)}
      titleClassName="pt-4 text-lg leading-6 font-medium justify-center!"
    >
      <SectionForm
        getFieldProps={getFieldProps}
        modalMode={modalMode}
        isLoading={isLoading}
        onClose={closeSectionModal}
        onSubmit={handleSubmit}
      />
    </Modal>
  );
};
